import React, { useEffect, useState } from 'react'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'

import { PageLink, PageTitle } from '../../../../../_metronic/layout/core'
import { FiltersRequisites } from './../../components/Filters'
import { IFilter } from './../../components/Filters/Filters.types'
import { fetchRequisitesHistoryLogs } from './../../api/fetchLogs'
import { Table } from '../../../../../components/Table'
import { TableHead } from '../../../../../components/Table/TableHead'
import { TableBody } from '../../../../../components/Table/TableBody'
import { IRequisiteHistoryElement } from './../../api/logs.types'
import { convertToUTCByMSK, convertUTCtoMSK } from '../../../../../utils'
import { getDefaultDateRange } from '../../../../../utils/getDefaultDateRange'
import { downloadFile } from '../../../../../utils/downloadFile'
import { REQUISITE_EVENT_NAME } from '../../../../../constants'
import { isApiErrorTicketToken } from '../../api/errors'
import { TablePagination } from '../../../../../components/Table/TablePagination'
import { ClientCell } from '../../../../common/ClientCell'

const AppSwal = withReactContent(Swal)

const breadCrumbs: Array<PageLink> = [
  {
    title: 'Реквизиты',
    path: '/logs/requisites',
    isSeparator: false,
    isActive: true,
  },
]

const ITEMS_PER_PAGE = 100
const DEFAULT_PAGE = 0

type DateFilter = { dateStart?: Date; dateEnd?: Date }

export const Requisites = () => {
  const { startDate: defaultStartDate, endDate: defaultEndDate } = getDefaultDateRange()
  const initialFilterState = { dateStart: defaultStartDate, dateEnd: defaultEndDate }

  const [filters, setFilters] = useState<IFilter>(initialFilterState)
  const [isFiltersApplied, setFiltersApplied] = useState<boolean>(true)
  const [requisitesHistoryLogs, setRequisitesHistoryLogs] = useState<IRequisiteHistoryElement[]>([])

  const [totalRows, setTotalRows] = useState(0)
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE)
  const [isLoading, setIsLoading] = useState(false)

  const countPages = Math.ceil(totalRows / ITEMS_PER_PAGE)

  useEffect(() => {
    fetchData(currentPage, filters)
  }, [])

  const fetchData = async (page = DEFAULT_PAGE, filterParams: DateFilter = {}) => {
    setIsLoading(true)

    try {
      const params = {
        ...filterParams,
        page: page + 1,
        dateStart: filterParams?.dateStart ? convertToUTCByMSK(filterParams.dateStart) : null,
        dateEnd: filterParams?.dateEnd ? convertToUTCByMSK(filterParams.dateEnd) : null,
      }

      const { data } = await fetchRequisitesHistoryLogs(params)

      if (data.success) {
        setRequisitesHistoryLogs(data.requisiteHistory)
        setTotalRows(data.totalRows)
      }
    } catch (error) {
      if (isApiErrorTicketToken(error)) {
        AppSwal.fire({
          icon: 'error',
          title: 'Ошибка',
          text: 'Некорректный Тикет Токен',
        })
      }
      console.error('Ошибка при загрузке данных:', error)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const handleApplyFilters = async () => {
    try {
      await fetchData(DEFAULT_PAGE, filters)
      setCurrentPage(DEFAULT_PAGE)
      setFiltersApplied(true)
    } catch (error) {
      console.error('Ошибка при применении фильтров:', error)
    }
  }

  const handleFiltersChange = async (newFilters: IFilter) => {
    setFilters(newFilters)

    const validFilters = Object.entries(newFilters).filter(([_, value]) => !!value)
    const newKeys = validFilters.map(([key]) => key)
    const initialKeys = Object.keys(initialFilterState)

    if (newKeys.length === initialKeys.length && newKeys.some((key) => initialKeys.includes(key))) {
      setFiltersApplied(true)
    } else {
      setFiltersApplied(false)
    }
  }

  const handlePageChange = async (selected: number) => {
    if (isFiltersApplied) {
      const newPage = selected
      await fetchData(newPage, filters)
      setCurrentPage(newPage)
    } else {
      await handleApplyFilters()
    }
  }

  const handleResetFilters = async () => {
    const newFilters = {
      dateStart: defaultStartDate,
      dateEnd: defaultEndDate,
    }

    setFilters(newFilters)
    setCurrentPage(DEFAULT_PAGE)
    setFiltersApplied(true)
    await fetchData(DEFAULT_PAGE, newFilters)
  }

  const exportRequisiteHistory = async () => {
    AppSwal.fire({
      title: 'Экспорт в процессе...',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    })

    try {
      const params = {
        ...filters,
        page: currentPage,
        dateStart: filters.dateStart ? convertToUTCByMSK(filters.dateStart) : null,
        dateEnd: filters.dateEnd ? convertToUTCByMSK(filters.dateEnd) : null,
        xlsx: true,
      }

      const { data: blob } = await fetchRequisitesHistoryLogs(params)
      downloadFile(blob, 'requisite_events', 'xlsx')
    } catch (error) {
      AppSwal.fire({
        icon: 'error',
        title: 'Ошибка',
        text: `Не удалось экспортировать данные: ${error}`,
      })
    } finally {
      AppSwal.close()
    }
  }

  return (
    <div>
      <div className='card sticky-top-tickets mb-7'>
        <PageTitle breadcrumbs={breadCrumbs} />
        <div className='card-header border-0 pt-5 pb-2'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>Логи действий с реквизитами</span>
          </h3>
        </div>
      </div>

      <div className='card mb-7'>
        <div className='card-body border-0 pt-5'>
          <FiltersRequisites
            filters={filters}
            hasExport
            onApplyFilters={handleApplyFilters}
            onFiltersChange={handleFiltersChange}
            onResetFilter={handleResetFilters}
            onExport={exportRequisiteHistory}
          />
        </div>
      </div>

      <div className='row g-5 g-xl-8 mb-7'>
        <div className='col'>
          <div className='card'>
            <div className='card-body'>
              <div className='card-rounded w-100'>
                <div className='d-flex flex-column'>
                  <Table>
                    <TableHead>
                      <tr>
                        <th>Дата/Время</th>
                        <th>Реквизит</th>
                        <th>Клиент/Сотрудник</th>
                        <th>Тикет токен</th>
                        <th>External user id</th>
                        <th>Мерчант</th>
                        <th>Действие</th>
                      </tr>
                    </TableHead>
                    <TableBody>
                      {isLoading ? (
                        <tr>
                          <td colSpan={7}>Идет загрузка...</td>
                        </tr>
                      ) : requisitesHistoryLogs.length ? (
                        requisitesHistoryLogs.map(
                          (requisitesHistoryLog: IRequisiteHistoryElement, index) => (
                            <tr key={index}>
                              <td>{convertUTCtoMSK(requisitesHistoryLog.createdAt)}</td>
                              <td>{requisitesHistoryLog.requisite?.name}</td>
                              <td>{requisitesHistoryLog?.user?.uuid}</td>
                              <td>{requisitesHistoryLog?.paymentTicket?.token || '-'}</td>
                              <ClientCell
                                externalId={requisitesHistoryLog?.externalUser?.externalUserId}
                                token={requisitesHistoryLog?.externalUser?.clientToken}
                              />
                              <td>{requisitesHistoryLog?.merchant?.title || '-'}</td>
                              <td>{REQUISITE_EVENT_NAME[requisitesHistoryLog.event]}</td>
                            </tr>
                          )
                        )
                      ) : (
                        <tr>
                          <td colSpan={7}>Данных нет.</td>
                        </tr>
                      )}
                    </TableBody>
                  </Table>
                  <TablePagination
                    currentPage={currentPage}
                    pageCount={countPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
